import { doc, getDocFromServer } from "firebase/firestore";

import { db } from "./firebase";
import { buildPublicIdentitySnapshot, normalizeLegacyIdentity } from "./identity";
import type {
  AccountType,
  IdentityEntitlements,
  LegacyAccountType,
  PublicIdentitySnapshot,
  VerificationStatus,
} from "../types/identity";
import type { AccountRecord } from "../types/account";

export const POST_USERNAME_PATTERN = /^[a-z0-9_]{3,16}$/;

export type CanonicalPostAuthor = {
  authorUid: string;
  authorEmail: string;
  authorUsername: string;
  authorPhotoURL: string;
  authorAccountType: AccountType;
  authorVerificationStatus: VerificationStatus;
  authorEntitlements?: IdentityEntitlements;
  /** @deprecated Compatibility snapshot for historical student/teacher posts. */
  authorRole?: LegacyAccountType;
  /** @deprecated Compatibility projection for historical teacher posts. */
  teacherVerified: boolean;
  track: string;
};

/** The only author shape accepted by new post creation flows. */
export type PostAuthorAttribution = {
  authorUid: string;
  authorIdentityId: string;
  authorSnapshot: PublicIdentitySnapshot;
};

export function isPostAuthorAttribution(value: unknown): value is PostAuthorAttribution {
  if (!value || typeof value !== "object") return false;
  const author = value as Partial<PostAuthorAttribution>;
  return typeof author.authorUid === "string"
    && typeof author.authorIdentityId === "string"
    && !!author.authorSnapshot
    && typeof author.authorSnapshot === "object"
    && author.authorSnapshot.identityId === author.authorIdentityId;
}

export class PostProfileValidationError extends Error {
  code = "post/profile-invalid";

  constructor(message = "Complete ou actualise ton profil avant de publier.") {
    super(message);
    this.name = "PostProfileValidationError";
  }
}

export function validateCanonicalPostProfile(
  uid: string,
  email: string | null | undefined,
  data: Record<string, unknown> | null
): CanonicalPostAuthor {
  if (!data) {
    throw new PostProfileValidationError("Profil introuvable. Complete d'abord ton profil.");
  }

  if (typeof data.username !== "string") {
    throw new PostProfileValidationError("Definis d'abord un @username valide dans le profil.");
  }
  if (data.photoURL != null && typeof data.photoURL !== "string") {
    throw new PostProfileValidationError("Actualise ta photo de profil avant de publier.");
  }

  const username = data.username.trim().toLowerCase();
  const identity = normalizeLegacyIdentity(data);
  const accountType = identity.accountType;
  const photoURL = data.photoURL == null ? "" : data.photoURL.trim();

  if (!POST_USERNAME_PATTERN.test(username)) {
    throw new PostProfileValidationError("Definis d'abord un @username valide dans le profil.");
  }
  if (!accountType) {
    throw new PostProfileValidationError("Actualise ton type de compte dans le profil avant de publier.");
  }
  if (photoURL.length > 2048) {
    throw new PostProfileValidationError("Actualise ta photo de profil avant de publier.");
  }

  const legacyRole = accountType === "student" || accountType === "teacher"
    ? accountType
    : undefined;

  return {
    authorUid: uid,
    authorEmail: String(data.email ?? email ?? ""),
    authorUsername: username,
    authorPhotoURL: photoURL,
    authorAccountType: accountType,
    authorVerificationStatus: identity.verificationStatus,
    ...(identity.entitlements ? { authorEntitlements: identity.entitlements } : {}),
    ...(legacyRole ? { authorRole: legacyRole } : {}),
    teacherVerified: accountType === "teacher" && identity.verificationStatus === "verified",
    track: accountType === "student" ? String(data.track ?? "") : "",
  };
}

function validatePublicAccount(accountId: string, account: AccountRecord | null | undefined) {
  if (!account || account.accountId !== accountId || account.status !== "active") {
    throw new PostProfileValidationError("Le compte public est indisponible. Actualise tes comptes puis reessaie.");
  }
  if (!account.accountType || !account.displayName?.trim()) {
    throw new PostProfileValidationError("Le compte public est incomplet. Actualise tes comptes puis reessaie.");
  }

  const handle = account.username?.trim().toLowerCase() ?? "";
  if (handle && !POST_USERNAME_PATTERN.test(handle)) {
    throw new PostProfileValidationError("Le @handle du compte public est invalide. Actualise tes comptes puis reessaie.");
  }
}

export function buildPostAuthorAttribution(
  uid: string,
  account: AccountRecord
): PostAuthorAttribution {
  validatePublicAccount(account.accountId, account);
  return {
    authorUid: uid,
    authorIdentityId: account.accountId,
    authorSnapshot: buildPublicIdentitySnapshot({
      accountId: account.accountId,
      accountType: account.accountType,
      displayName: account.displayName.trim(),
      username: account.username?.trim().toLowerCase(),
      photoURL: account.photoURL,
      verificationStatus: account.verificationStatus,
      entitlements: account.entitlements,
      badges: account.badges,
    }),
  };
}

export async function loadCanonicalPostAuthor(
  uid: string,
  _email?: string | null,
  accountId = uid
): Promise<PostAuthorAttribution> {
  // Public post attribution is resolved from the server-owned account relation
  // and account projection, never from users/{uid} or client-selected fields.
  const accessSnapshot = await getDocFromServer(
    doc(db, "accountAccess", `${uid}__${accountId}`)
  );
  const accountSnapshot = await getDocFromServer(doc(db, "accounts", accountId));
  const access = accessSnapshot.exists() ? accessSnapshot.data() as Record<string, unknown> : null;
  const account = accountSnapshot.exists() ? accountSnapshot.data() as AccountRecord : null;

  if (
    !access
    || access.uid !== uid
    || access.accountId !== accountId
    || access.status !== "active"
    || !["owner", "admin", "editor"].includes(String(access.role))
  ) {
    throw new PostProfileValidationError("Tu n'as pas acces a ce compte public.");
  }

  validatePublicAccount(accountId, account);
  return buildPostAuthorAttribution(uid, account!);
}

export function firebaseErrorDetails(error: unknown) {
  const value = error as { code?: unknown; message?: unknown } | null;
  return {
    code: typeof value?.code === "string" ? value.code : "unknown",
    message: typeof value?.message === "string" ? value.message : String(error),
  };
}

export function safePostErrorMessage(error: unknown, fallback: string) {
  if (error instanceof PostProfileValidationError) return error.message;

  const code = firebaseErrorDetails(error).code;
  if (code === "storage/unauthorized" || code === "permission-denied") {
    return "La publication a ete refusee. Actualise ton profil puis reessaie.";
  }
  if (code === "storage/timeout") {
    return "L'envoi de l'image a expire. Verifie ta connexion puis reessaie.";
  }
  if (code === "storage/quota-exceeded") {
    return "Le stockage des photos est temporairement indisponible.";
  }
  if (code === "storage/canceled") {
    return "L'envoi de l'image a ete annule.";
  }
  if (code === "unavailable" || code === "deadline-exceeded") {
    return "Connexion interrompue. Verifie ton reseau puis reessaie.";
  }
  if (
    code === "post/file-not-found" ||
    code === "post/file-read-failed" ||
    code === "post/file-empty" ||
    code === "post/file-too-large" ||
    code === "post/image-picker-failed" ||
    code === "post/image-manipulation-failed"
  ) {
    return "Impossible de lire cette image. Choisis une autre photo.";
  }
  if (code === "storage/unknown" || code === "post/download-url-failed") {
    return "L'envoi de la photo a echoue. Verifie ta connexion puis reessaie.";
  }
  return fallback;
}
